import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Calendar, Clock, Save, ToggleLeft, ToggleRight, Loader2, AlertCircle, Key, RefreshCw, Copy, ChevronDown, FileText } from 'lucide-react';

export default function ExamSettingsPage() {
  const [settings, setSettings] = useState({ examDate: '', startTime: '', endTime: '', isExamLive: false, examCode: '', activeTestId: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [codeLoading, setCodeLoading] = useState(false);
  const [tests, setTests] = useState([]);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const { data } = await api.get('/exam-settings/admin');
      const s = data.data || {};

      // Fetch active tests for dropdown
      const testsRes = await api.get('/tests');
      setTests(testsRes.data.data || []);

      // Helper to convert Date to local datetime-local string (YYYY-MM-DDTHH:mm)
      const toLocalISOString = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const tzOffset = date.getTimezoneOffset() * 60000;
        return new Date(date.getTime() - tzOffset).toISOString().slice(0, 16);
      };

      setSettings({
        examDate: s.examDate ? s.examDate.slice(0, 10) : '',
        startTime: toLocalISOString(s.startTime),
        endTime: toLocalISOString(s.endTime),
        isExamLive: s.isExamLive || false,
        examCode: s.examCode || '',
        activeTestId: s.activeTestId || ''
      });
    } catch {
      toast.error('Failed to load exam settings');
    } finally {
      setLoading(false);
    }
  };

  const generateCode = async () => {
    setCodeLoading(true);
    try {
      const { data } = await api.post('/exam-settings/generate-code');
      setSettings(s => ({ ...s, examCode: data.examCode }));
      toast.success('New exam code generated!');
    } catch (err) {
      toast.error('Failed to generate code');
    } finally {
      setCodeLoading(false);
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(settings.examCode);
    toast.success('Copied to clipboard!');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Ensure we send full ISO strings with timezone info
      const startTime = settings.startTime ? new Date(settings.startTime).toISOString() : null;
      const endTime = settings.endTime ? new Date(settings.endTime).toISOString() : null;

      await api.put('/exam-settings', {
        examDate: settings.examDate || null,
        startTime,
        endTime,
        isExamLive: settings.isExamLive,
        activeTestId: settings.activeTestId || null
      });
      toast.success('Exam settings saved successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleLive = async () => {
    const newVal = !settings.isExamLive;
    setSettings(s => ({ ...s, isExamLive: newVal }));
    try {
      await api.put('/exam-settings', { isExamLive: newVal });
      toast.success(newVal ? '🟢 Exam is now LIVE' : '🔴 Exam is now CLOSED');
    } catch {
      setSettings(s => ({ ...s, isExamLive: !newVal }));
      toast.error('Failed to update live status');
    }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="loader"></div></div>;

  const now = new Date();
  const startTime = settings.startTime ? new Date(settings.startTime) : null;
  const endTime = settings.endTime ? new Date(settings.endTime) : null;
  let examStatus = 'Not Configured';
  let statusColor = 'text-gray-500 bg-gray-100';
  if (startTime && endTime) {
    if (now < startTime) { examStatus = 'Upcoming'; statusColor = 'text-amber-700 bg-amber-100'; }
    else if (now >= startTime && now <= endTime) { examStatus = 'In Progress'; statusColor = 'text-green-700 bg-green-100'; }
    else { examStatus = 'Ended'; statusColor = 'text-red-700 bg-red-100'; }
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Exam Settings</h1>
        <p className="text-gray-500 mt-1">Control exam date, time window, and access</p>
      </div>

      {/* Live Status Card */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-gray-900">Exam Live Status</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {settings.isExamLive
                ? 'Exam is currently OPEN — students can log in and start exam'
                : 'Exam is CLOSED — students cannot access exam'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-xs font-semibold px-3 py-1 rounded-full ${statusColor}`}>{examStatus}</span>
            <button onClick={toggleLive}
              className={`transition-colors ${settings.isExamLive ? 'text-green-500 hover:text-green-700' : 'text-gray-400 hover:text-gray-600'}`}>
              {settings.isExamLive ? <ToggleRight size={36} /> : <ToggleLeft size={36} />}
            </button>
          </div>
        </div>
      </div>

      {/* Settings Form */}
      <div className="card">
        <h2 className="font-semibold text-gray-900 mb-5">Exam Schedule</h2>
        <form onSubmit={handleSave} className="space-y-5">

          {/* Active Test Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <FileText size={14} className="inline mr-1" /> Active Test
            </label>
            <div className="relative">
              <select
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black appearance-none bg-white pr-10"
                value={settings.activeTestId || ''}
                onChange={e => setSettings(s => ({ ...s, activeTestId: e.target.value || '' }))}
              >
                <option value="">-- Choose a test for this exam session --</option>
                {tests.map(t => (
                  <option key={t._id} value={t._id}>{t.title} ({t.totalQuestions} Qs)</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
            </div>
            <p className="text-xs text-gray-400 mt-1">Select which test students will take when exam is live</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Calendar size={14} className="inline mr-1" /> Exam Date
            </label>
            <input type="date" className="input-field"
              value={settings.examDate}
              onChange={e => setSettings(s => ({ ...s, examDate: e.target.value }))} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Clock size={14} className="inline mr-1" /> Start Date & Time
              </label>
              <input type="datetime-local" className="input-field"
                value={settings.startTime}
                onChange={e => setSettings(s => ({ ...s, startTime: e.target.value }))} />
              <p className="text-xs text-gray-400 mt-1">Students cannot log in before this time</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Clock size={14} className="inline mr-1" /> End Date & Time
              </label>
              <input type="datetime-local" className="input-field"
                value={settings.endTime}
                onChange={e => setSettings(s => ({ ...s, endTime: e.target.value }))} />
              <p className="text-xs text-gray-400 mt-1">Exam auto-closes after this time</p>
            </div>
          </div>

          {startTime && endTime && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              <AlertCircle size={14} className="inline mr-1" />
              Exam window: <strong>{startTime.toLocaleString('en-IN')}</strong> → <strong>{endTime.toLocaleString('en-IN')}</strong>
              {' '}({Math.round((endTime - startTime) / 60000)} minutes)
            </div>
          )}

          <button type="submit" disabled={saving}
            className="btn-primary flex items-center gap-2">
            {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
            Save Settings
          </button>
        </form>
      </div>

      {/* Exam Code Card */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-semibold text-gray-900 flex items-center gap-2"><Key size={18} /> Exam Code</h2>
            <p className="text-sm text-gray-500 mt-0.5">Share this code with students for session-based access</p>
          </div>
          <button onClick={generateCode} disabled={codeLoading}
            className="btn-primary flex items-center gap-2 text-sm">
            {codeLoading ? <Loader2 className="animate-spin" size={16} /> : <RefreshCw size={16} />}
            Generate New Code
          </button>
        </div>
        {settings.examCode ? (
          <div className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-xl px-5 py-4">
            <span className="font-mono text-3xl font-extrabold text-black tracking-[0.3em] flex-1">{settings.examCode}</span>
            <button onClick={copyCode} className="text-gray-400 hover:text-gray-700 transition-colors" title="Copy">
              <Copy size={20} />
            </button>
          </div>
        ) : (
          <p className="text-sm text-gray-400 italic">No code generated yet. Click "Generate New Code" above.</p>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm text-gray-600 space-y-1">
        <p className="font-semibold text-gray-800 mb-2">How exam timing works:</p>
        <p>• Students login with Hall Ticket + Password at <strong>/login</strong></p>
        <p>• Before exam starts → they see a <strong>live countdown timer</strong></p>
        <p>• If exam has ended → login form is <strong>disabled</strong></p>
        <p>• If exam is live AND within the time window → they can start their test</p>
        <p>• The <strong>Live Status toggle</strong> above overrides all time checks when manually set</p>
      </div>
    </div>
  );
}
